import { useEffect, useRef, useState } from 'react';
import type { JSX, ReactNode } from 'react';

import type {
  FreigabeVorschau,
  RessourcenAnfrage,
  WorkPackageFachgruppeOption,
  WunschKandidat,
} from '@asas/domain';

import { anmeldeUrlMitRueckkehr } from '../daten/entwurf';
import { klassifiziereFehler } from '../daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from '../daten/serialisierung';
import { sperrtWeitereVersuche } from '../fachgruppen/fachgruppen';
import type { FehlerBefund } from '../daten/fehler';
import { trpc, trpcAnfrageEntschieden } from '../trpc';

import {
  ablehnungEingabe,
  anfrageEingabe,
  entschiedenText,
  entscheidungsFormular,
  freigabeEingabe,
  leeresFormular,
  meldungFuerAnfrage,
  meldungFuerEntscheidung,
  versionenVon,
  vorschauEingabe,
} from './anfragen';
import type {
  AnfrageFormular,
  AnfrageHinweisAnzeige,
  EntscheidungsFehler,
  EntscheidungsFormular,
  FormularFehler,
} from './anfragen';
import { AnfrageFormularAnsicht, AnfragenListe, EntscheidungAnsicht } from './AnfrageAnsicht';

export interface AnfrageBereichProps {
  readonly projectId: string;
  readonly arbeitspaket: {
    readonly id: string;
    readonly name: string;
    readonly fachgruppeId: string;
  };
  readonly fachgruppen: readonly WorkPackageFachgruppeOption[];
  readonly onSchliessen: () => void;
  /** Meldet, ob gerade gesendet wird — der Wechsel zu einem anderen Arbeitspaket ist dann gesperrt. */
  readonly onSendet?: (sendet: boolean) => void;
}

/**
 * Eine Fachgruppe für EIN Arbeitspaket anonym anfragen (AC-404).
 *
 * Gezeigt wird der Bereich erst nach dem Klick „Ressourcen anfragen" und nur
 * mit `resource_allocation:request` (`darfRessourcenAnfragen`); deshalb lädt
 * er die Wunschkandidaten beim Einhängen — einmal, auch unter StrictMode.
 *
 * Nach dem Rezept aus AC-016: das Anlegen läuft über die GETEILTE
 * Warteschlange je Projekt (der Server sperrt dieselbe Projektzeile), jeder
 * Fehler durch `klassifiziereFehler`, nach Erfolg gilt die SERVERANTWORT. Ein
 * Hinweis (AK-6 Duplikat, AK-7 großer Umfang) ist kein Fehler: nichts wurde
 * angelegt, das Formular bleibt stehen; nur AK-7 lässt sich bestätigen.
 */
export function AnfrageBereich({
  projectId,
  arbeitspaket,
  fachgruppen,
  onSchliessen,
  onSendet,
}: AnfrageBereichProps): JSX.Element {
  const [kandidaten, setKandidaten] = useState<readonly WunschKandidat[]>([]);
  const [formular, setFormular] = useState<AnfrageFormular>(
    leeresFormular(arbeitspaket.fachgruppeId),
  );
  const [fehlt, setFehlt] = useState<FormularFehler | null>(null);
  const [hinweis, setHinweis] = useState<AnfrageHinweisAnzeige | null>(null);
  const [befund, setBefund] = useState<FehlerBefund | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const [angelegt, setAngelegt] = useState<readonly RessourcenAnfrage[]>([]);
  const gestartet = useRef(false);

  const zugriffVerweigert = sperrtWeitereVersuche(befund);

  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;
    trpc.anfragen.wunschKandidaten
      .query()
      .then(setKandidaten)
      .catch((error: unknown) => {
        const neu = klassifiziereFehler(error, { schreibzugriff: false });
        setBefund(neu);
        setFehler(neu.meldung);
      });
  }, []);

  async function senden(umfangBestaetigt: boolean): Promise<void> {
    const eingabe = anfrageEingabe(
      formular,
      { projectId, workPackageId: arbeitspaket.id },
      umfangBestaetigt,
    );

    if ('fehlt' in eingabe) {
      setFehlt(eingabe.fehlt);
      return;
    }

    setFehlt(null);
    setFehler(null);
    setSendet(true);
    onSendet?.(true);

    try {
      const ergebnis = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('project', projectId),
        () => trpc.anfragen.anlegen.mutate(eingabe),
      );

      if (ergebnis.ergebnis === 'angelegt') {
        setAngelegt((alt) => [...alt, ergebnis.anfrage]);
        setHinweis(null);
        setFormular(leeresFormular(arbeitspaket.fachgruppeId));
      } else {
        setHinweis(ergebnis);
      }
    } catch (error) {
      const neu = klassifiziereFehler(error, { schreibzugriff: true });
      setBefund(neu);
      setFehler(meldungFuerAnfrage(neu));
      setHinweis(null);
    } finally {
      setSendet(false);
      onSendet?.(false);
    }
  }

  return (
    <section aria-label={`Anfrage ${arbeitspaket.name}`}>
      {fehler !== null && <p role="alert">{fehler}</p>}
      {befund?.neuAnmelden === true && <a href={anmeldeUrlMitRueckkehr('/')}>Erneut anmelden</a>}
      <div role="status" aria-live="polite">
        {sendet && <p>Anfrage wird gesendet …</p>}
        {angelegt.length > 0 && !sendet && <p>Anfrage gesendet. Status: angefragt.</p>}
      </div>
      <AnfrageFormularAnsicht
        arbeitspaketName={arbeitspaket.name}
        fachgruppen={fachgruppen}
        kandidaten={kandidaten}
        formular={formular}
        gesperrt={sendet || zugriffVerweigert}
        sendet={sendet}
        fehlt={fehlt}
        hinweis={hinweis}
        onFormular={setFormular}
        onAbsenden={() => void senden(false)}
        onUmfangBestaetigen={() => void senden(true)}
        onHinweisSchliessen={() => {
          setHinweis(null);
        }}
        onAbbrechen={onSchliessen}
      />
      {angelegt.length > 0 && (
        <AnfragenListe titel="Gerade angefragt" anfragen={angelegt} leerText="" />
      )}
    </section>
  );
}

/**
 * Die eigenen Anfragen (AC-404) — geladen erst auf Klick, nach einem 403
 * gesperrt bis zum Neuladen der Seite (Muster `FachgruppenBereich`). Seit
 * AC-405 mit Ausgang und „Zurückziehen" für noch offene Anfragen (AK 5).
 */
export function MeineAnfragen(): JSX.Element {
  return (
    <AnfragenLaden
      schluessel="meine"
      titel="Meine Anfragen"
      knopf="Meine Anfragen laden"
      leerText="Sie haben noch keine offenen oder entschiedenen Anfragen."
      laden={() => trpc.anfragen.meine.query()}
      aktion={(anfrage, neuLaden) =>
        anfrage.freigabeStatus === 'angefragt' ? (
          <Zurueckziehen anfrage={anfrage} onErledigt={neuLaden} />
        ) : null
      }
    />
  );
}

/**
 * Die offenen Anfragen an eine Fachgruppe (AK-3) — für die Fachgruppenleitung.
 * Mit `darfEntscheiden` (AC-405) trägt jede Zeile „Entscheiden".
 */
export function OffeneAnfragen({
  fachgruppeId,
  darfEntscheiden = false,
  onEntschieden,
}: {
  readonly fachgruppeId: string;
  readonly darfEntscheiden?: boolean;
  /** Nach einer Entscheidung: die Last hat sich geändert (das Wochenraster lädt neu, AC-016). */
  readonly onEntschieden?: (() => void) | undefined;
}): JSX.Element {
  const [offen, setOffen] = useState<string | null>(null);

  return (
    <AnfragenLaden
      schluessel={fachgruppeId}
      titel="Offene Anfragen an diese Fachgruppe"
      knopf="Offene Anfragen laden"
      leerText="Für diese Fachgruppe liegen keine offenen Anfragen vor."
      laden={() => trpc.anfragen.offene.query({ fachgruppeId })}
      aktion={
        darfEntscheiden
          ? (anfrage, neuLaden) =>
              offen === anfrage.anfrageId ? (
                <EntscheidungBereich
                  anfrage={anfrage}
                  fachgruppeId={fachgruppeId}
                  onSchliessen={() => {
                    setOffen(null);
                  }}
                  onEntschieden={() => {
                    setOffen(null);
                    neuLaden();
                    onEntschieden?.();
                  }}
                />
              ) : (
                <button
                  type="button"
                  disabled={offen !== null}
                  onClick={() => {
                    setOffen(anfrage.anfrageId);
                  }}
                >
                  Entscheiden
                </button>
              )
          : undefined
      }
    />
  );
}

/**
 * Eine Anfrage entscheiden (AC-405). Geöffnet erst auf „Entscheiden"; lädt dann
 * die Mitglieder der Fachgruppe als Auswahl (`fachgruppen.get`, heute im
 * Planungskalender). Schreibzugriffe über die GETEILTE Warteschlange je Anfrage;
 * nach Erfolg gilt der Stand des Servers (die Liste lädt neu, AC-016). Ist die
 * Anfrage inzwischen entschieden oder zurückgezogen, nennt der Bereich wer,
 * wann und wie (AK 5, AK 6) und sperrt sich.
 */
function EntscheidungBereich({
  anfrage,
  fachgruppeId,
  onSchliessen,
  onEntschieden,
}: {
  readonly anfrage: RessourcenAnfrage;
  readonly fachgruppeId: string;
  readonly onSchliessen: () => void;
  readonly onEntschieden: () => void;
}): JSX.Element {
  const [kandidaten, setKandidaten] = useState<readonly WunschKandidat[]>([]);
  const [formular, setFormular] = useState<EntscheidungsFormular>(entscheidungsFormular(anfrage));
  const [fehlt, setFehlt] = useState<EntscheidungsFehler | null>(null);
  const [vorschau, setVorschau] = useState<FreigabeVorschau | null>(null);
  const [ueberlast, setUeberlast] = useState<FreigabeVorschau | null>(null);
  const [befund, setBefund] = useState<FehlerBefund | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [entschieden, setEntschieden] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const gestartet = useRef(false);

  useEffect(() => {
    if (gestartet.current) return;
    gestartet.current = true;
    trpc.fachgruppen.get
      .query({ id: fachgruppeId })
      .then((details) => {
        setKandidaten(details.members.map((m) => ({ employeeId: m.employeeId, name: m.name })));
      })
      .catch((error: unknown) => {
        const neu = klassifiziereFehler(error, { schreibzugriff: false });
        setBefund(neu);
        setFehler(neu.meldung);
      });
  }, [fachgruppeId]);

  const gesperrt = laeuft || entschieden !== null || sperrtWeitereVersuche(befund);

  async function ausfuehren(
    aufruf: () => Promise<void>,
    { schreibzugriff }: { readonly schreibzugriff: boolean },
  ): Promise<void> {
    setFehler(null);
    setLaeuft(true);

    try {
      await aufruf();
    } catch (error) {
      const bereits = trpcAnfrageEntschieden(error);
      if (bereits !== null) {
        setEntschieden(entschiedenText(bereits));
        setUeberlast(null);
        return;
      }
      const neu = klassifiziereFehler(error, { schreibzugriff });
      setBefund(neu);
      setFehler(meldungFuerEntscheidung(neu));
    } finally {
      setLaeuft(false);
    }
  }

  function pruefen(): void {
    const eingabe = vorschauEingabe(formular, anfrage);
    setFehlt('fehlt' in eingabe ? eingabe.fehlt : null);
    if ('fehlt' in eingabe) return;

    void ausfuehren(
      async () => {
        setVorschau(await trpc.anfragen.vorschau.query(eingabe));
      },
      { schreibzugriff: false },
    );
  }

  function freigeben(ueberlastBestaetigt: boolean): void {
    const eingabe = freigabeEingabe(formular, anfrage, ueberlastBestaetigt);
    setFehlt('fehlt' in eingabe ? eingabe.fehlt : null);
    if ('fehlt' in eingabe) return;

    void ausfuehren(
      async () => {
        const ergebnis = await geteilterSerialisierer.reihe(
          entitaetsSchluessel('anfrage', anfrage.anfrageId),
          () => trpc.anfragen.freigeben.mutate(eingabe),
        );

        if (ergebnis.ergebnis === 'ueberlast') {
          setUeberlast({ wochen: ergebnis.wochen, ueberlastWochen: ergebnis.ueberlastWochen });
          return;
        }
        setUeberlast(null);
        onEntschieden();
      },
      { schreibzugriff: true },
    );
  }

  function ablehnen(): void {
    const eingabe = ablehnungEingabe(formular, anfrage);
    setFehlt('fehlt' in eingabe ? eingabe.fehlt : null);
    if ('fehlt' in eingabe) return;

    void ausfuehren(
      async () => {
        await geteilterSerialisierer.reihe(entitaetsSchluessel('anfrage', anfrage.anfrageId), () =>
          trpc.anfragen.ablehnen.mutate(eingabe),
        );
        onEntschieden();
      },
      { schreibzugriff: true },
    );
  }

  return (
    <section aria-label="Entscheidung">
      {fehler !== null && <p role="alert">{fehler}</p>}
      {befund?.neuAnmelden === true && <a href={anmeldeUrlMitRueckkehr('/')}>Erneut anmelden</a>}
      <div role="status" aria-live="polite">
        {laeuft && <p>Wird übertragen …</p>}
        {entschieden !== null && <p>{entschieden}</p>}
      </div>
      <EntscheidungAnsicht
        anfrage={anfrage}
        kandidaten={kandidaten}
        formular={formular}
        vorschau={vorschau}
        ueberlast={ueberlast}
        gesperrt={gesperrt}
        sendet={laeuft}
        fehlt={fehlt}
        onFormular={(neu) => {
          setFormular(neu);
          // Eine Vorschau gilt dem Stand, für den sie gerechnet wurde.
          setVorschau(null);
        }}
        onVorschau={pruefen}
        onFreigeben={() => {
          freigeben(false);
        }}
        onUeberlastBestaetigen={() => {
          freigeben(true);
        }}
        onUeberlastVerwerfen={() => {
          setUeberlast(null);
        }}
        onAblehnen={ablehnen}
        onAbbrechen={onSchliessen}
      />
      {entschieden !== null && (
        <button type="button" onClick={onEntschieden}>
          Liste neu laden
        </button>
      )}
    </section>
  );
}

/**
 * Eine eigene, noch offene Anfrage zurückziehen (AC-405, AK 5) — mit einer
 * Rückfrage, weil die Wochen danach gelöscht sind.
 */
function Zurueckziehen({
  anfrage,
  onErledigt,
}: {
  readonly anfrage: RessourcenAnfrage;
  readonly onErledigt: () => void;
}): JSX.Element {
  const [fragt, setFragt] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [befund, setBefund] = useState<FehlerBefund | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function zurueckziehen(): Promise<void> {
    setLaeuft(true);
    setMeldung(null);

    try {
      await geteilterSerialisierer.reihe(entitaetsSchluessel('anfrage', anfrage.anfrageId), () =>
        trpc.anfragen.zurueckziehen.mutate({
          anfrageId: anfrage.anfrageId,
          versionen: versionenVon(anfrage),
        }),
      );
      onErledigt();
    } catch (error) {
      const bereits = trpcAnfrageEntschieden(error);
      if (bereits !== null) {
        setMeldung(entschiedenText(bereits));
        return;
      }
      const neu = klassifiziereFehler(error, { schreibzugriff: true });
      setBefund(neu);
      setMeldung(meldungFuerEntscheidung(neu));
    } finally {
      setLaeuft(false);
      setFragt(false);
    }
  }

  return (
    <>
      {meldung !== null && <p role="alert">{meldung}</p>}
      {fragt ? (
        <span role="alertdialog" aria-label="Anfrage zurückziehen">
          Wirklich zurückziehen?{' '}
          <button type="button" disabled={laeuft} onClick={() => void zurueckziehen()}>
            Ja, zurückziehen
          </button>{' '}
          <button
            type="button"
            disabled={laeuft}
            onClick={() => {
              setFragt(false);
            }}
          >
            Nein
          </button>
        </span>
      ) : (
        <button
          type="button"
          disabled={laeuft || sperrtWeitereVersuche(befund)}
          onClick={() => {
            setFragt(true);
          }}
        >
          Zurückziehen
        </button>
      )}
    </>
  );
}

/**
 * Lädt auf Klick. Ein Wechsel von `schluessel` (andere Fachgruppe) blendet die
 * alte Liste aus, setzt aber eine 403-Sperre NICHT zurück — die gilt bis zum
 * Neuladen der Seite (apps/web/CLAUDE.md, Ansichten).
 */
function AnfragenLaden(props: {
  readonly schluessel: string;
  readonly titel: string;
  readonly knopf: string;
  readonly leerText: string;
  readonly laden: () => Promise<RessourcenAnfrage[]>;
  /** Eine Aktion je Zeile (AC-405); `neuLaden` holt danach den Stand des Servers. */
  readonly aktion?: ((anfrage: RessourcenAnfrage, neuLaden: () => void) => ReactNode) | undefined;
}): JSX.Element {
  const [geladen, setGeladen] = useState<{
    readonly fuer: string;
    readonly anfragen: readonly RessourcenAnfrage[];
  } | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [befund, setBefund] = useState<FehlerBefund | null>(null);

  async function laden(): Promise<void> {
    setLaedt(true);
    const fuer = props.schluessel;

    try {
      const anfragen = await props.laden();
      setGeladen({ fuer, anfragen });
      setBefund(null);
    } catch (error) {
      setBefund(klassifiziereFehler(error, { schreibzugriff: false }));
    } finally {
      setLaedt(false);
    }
  }

  const anfragen = geladen?.fuer === props.schluessel ? geladen.anfragen : null;

  return (
    <section aria-label={props.titel}>
      <h4>{props.titel}</h4>
      {befund !== null && <p role="alert">{befund.meldung}</p>}
      <p>
        <button
          type="button"
          disabled={laedt || sperrtWeitereVersuche(befund)}
          onClick={() => void laden()}
        >
          {laedt ? 'Wird geladen …' : anfragen === null ? props.knopf : 'Neu laden'}
        </button>
      </p>
      {anfragen !== null && (
        <AnfragenListe
          titel={props.titel}
          anfragen={anfragen}
          leerText={props.leerText}
          aktion={
            props.aktion === undefined
              ? undefined
              : (anfrage) =>
                  props.aktion?.(anfrage, () => {
                    void laden();
                  })
          }
        />
      )}
    </section>
  );
}
